import type { DayWisePricingModel, HotelContractModel } from "../../models"
import type { DayWisePricingGroupLocal } from "./hotelContractTypes"
import { WEEK_DAYS } from "./hotelContractTypes"

const defaultLocalGroups = (): DayWisePricingGroupLocal[] => [
    {
        day_wise_pricing_group_id: "group_default_weekdays",
        day_wise_pricing_group_name: "Weekdays",
        days: [1, 2, 3, 4, 5],
        flag_surchange_applied: false,
        surcharge_amount: null,
        flag_surcharge_amount_is_percentage: false,
        day_wise_pricing_group_surcharge: [],
        flag_disabled: false,
        flag_enable_day_group_in_rate_table: false,
        sequence: 1,
    },
    {
        day_wise_pricing_group_id: "group_default_weekends",
        day_wise_pricing_group_name: "Weekends",
        days: [6, 7],
        flag_surchange_applied: false,
        surcharge_amount: null,
        flag_surcharge_amount_is_percentage: false,
        day_wise_pricing_group_surcharge: [],
        flag_disabled: false,
        flag_enable_day_group_in_rate_table: false,
        sequence: 2,
    },
]

/** Map API / persisted contract rows to editor-local day-wise groups. */
export function dayWisePricingApiToLocal(api: DayWisePricingModel[] | undefined | null): DayWisePricingGroupLocal[] {
    if (api && api.length > 0) {
        return [...api]
            .map((group, idx) => ({
                day_wise_pricing_group_id: group.day_wise_pricing_group_id || `group_${Date.now()}_${idx}`,
                day_wise_pricing_group_name: group.day_wise_pricing_group_name || null,
                flag_surchange_applied: group.flag_surchange_applied,
                surcharge_amount: group.surcharge_amount,
                flag_surcharge_amount_is_percentage: group.flag_surcharge_amount_is_percentage === true,
                days: group.pricing_group_day?.map((day) => day.week_day_id) || [],
                day_wise_pricing_group_surcharge: (group.day_wise_pricing_group_surcharge || []).map((s) => ({
                    flag_different_surcharge: s.flag_different_surcharge,
                    surcharge_amount: s.surcharge_amount,
                    flag_surcharge_amount_is_percentage: s.flag_surcharge_amount_is_percentage === true,
                })),
                flag_disabled: false,
                flag_enable_day_group_in_rate_table: group.flag_enable_day_group_in_rate_table || false,
                sequence: typeof group.sequence === "number" && Number.isFinite(group.sequence) ? group.sequence : idx + 1,
            }))
            .sort(
                (a, b) =>
                    (a.sequence - b.sequence) || String(a.day_wise_pricing_group_id).localeCompare(String(b.day_wise_pricing_group_id)),
            )
    }
    return defaultLocalGroups();
}

/**
 * Minimal payload for UpdateHotelRoomPricing: group id + surcharge fields per row.
 * Client-only ids (`group_*`) become null, same as full contract save.
 */
export function localDayWiseGroupsToRoomPricingDayWisePatch(
    groups: DayWisePricingGroupLocal[],
): Array<{
    day_wise_pricing_group_id: string | null
    surcharge_amount: number | null
    flag_surcharge_amount_is_percentage: boolean
    day_wise_pricing_group_surcharge: {
        flag_different_surcharge: boolean
        surcharge_amount: number | null
        flag_surcharge_amount_is_percentage: boolean
    }[]
}> {
    return [...groups]
        .sort(
            (a, b) =>
                (a.sequence - b.sequence) || String(a.day_wise_pricing_group_id).localeCompare(String(b.day_wise_pricing_group_id)),
        )
        .map((g) => ({
            day_wise_pricing_group_id: g.day_wise_pricing_group_id?.startsWith("group_") ? null : g.day_wise_pricing_group_id ?? null,
            surcharge_amount: g.surcharge_amount ?? null,
            flag_surcharge_amount_is_percentage: g.flag_surcharge_amount_is_percentage === true,
            day_wise_pricing_group_surcharge: (g.day_wise_pricing_group_surcharge || []).map((s) => ({
                flag_different_surcharge: s.flag_different_surcharge,
                surcharge_amount: s.surcharge_amount ?? null,
                flag_surcharge_amount_is_percentage: s.flag_surcharge_amount_is_percentage === true,
            })),
        }))
}

/** Clear season/day-wise surcharge fields on persisted rows (e.g. any room type supports different surcharge). */
export function clearDayWiseSurchargesInApiModels(api: DayWisePricingModel[] | undefined | null): DayWisePricingModel[] {
    if (!api?.length) return []
    return api.map((row) => ({
        ...row,
        surcharge_amount: null,
        flag_surcharge_amount_is_percentage: false,
        flag_surchange_applied: false,
        day_wise_pricing_group_surcharge: [],
    }))
}

/** Apply {@link clearDayWiseSurchargesInApiModels} to every contract. */
export function contractsWithDayWiseSurchargesCleared(
    contracts: HotelContractModel[] | undefined | null,
): HotelContractModel[] {
    if (contracts == null || contracts.length === 0) {
        return contracts ?? []
    }
    return contracts.map((c) => ({
        ...c,
        day_wise_pricing: clearDayWiseSurchargesInApiModels(c.day_wise_pricing),
    }))
}

/** Same conversion as HotelContract onChange → contract.day_wise_pricing */
export function localDayWiseGroupsToApi(groups: DayWisePricingGroupLocal[]): DayWisePricingModel[] {
    return [...groups]
        .sort(
            (a, b) =>
                (a.sequence - b.sequence) || String(a.day_wise_pricing_group_id).localeCompare(String(b.day_wise_pricing_group_id)),
        )
        .map((group) => {
            return {
                day_wise_pricing_group_id: group.day_wise_pricing_group_id?.startsWith("group_") ? null : group.day_wise_pricing_group_id,
                day_wise_pricing_group_name: group.day_wise_pricing_group_name,
                flag_surchange_applied: group.flag_surchange_applied,
                surcharge_amount: group.surcharge_amount,
                flag_surcharge_amount_is_percentage: group.flag_surcharge_amount_is_percentage === true,
                day_wise_pricing_group_surcharge: (group.day_wise_pricing_group_surcharge || []).map((s) => ({
                    flag_different_surcharge: s.flag_different_surcharge,
                    surcharge_amount: s.surcharge_amount ?? null,
                    flag_surcharge_amount_is_percentage: s.flag_surcharge_amount_is_percentage === true,
                })),
                flag_enable_day_group_in_rate_table: group.flag_enable_day_group_in_rate_table,
                sequence: group.sequence,
                pricing_group_day: group.days.map((dayId) => {
                    const dayInfo = WEEK_DAYS.find((d) => d.id === dayId)
                    return {
                        pricing_group_day_id: null,
                        week_day_id: dayId,
                        week_day_name: dayInfo?.label || "",
                    }
                }),
            }
        })
}
